import { useQuery } from "@tanstack/react-query";
import MatchService from "../services/matchService";

/**
 * Hook to fetch matches with pagination and filters
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.limit - Items per page
 * @param {string} params.teamId - Filter by team ID
 * @param {string} params.status - Filter by match status
 * @param {string} params.sort - Sort field and direction
 * @returns {Object} - Query result with match data
 */
export const useGetMatches = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ["matches", params],
    queryFn: async () => {
      try {
        console.log('Fetching matches with params:', params);
        const response = await MatchService.getMatches(params);
        console.log('Server response data:', response);
        return response;
      } catch (error) {
        console.error('Error in matches API call:', error);
        if (error.response) {
          console.error('Response error data:', error.response.data);
          console.error('Response error status:', error.response.status);
        } else if (error.request) {
          console.error('No response received:', error.request);
        } else {
          console.error('Error setting up request:', error.message);
        }
        throw error;
      }
    },
    enabled: options?.enabled ?? true,
  });
};

/**
 * Hook to fetch match details by ID
 * @param {string} matchId - Match ID
 * @returns {Object} - Query result with match details
 */
export const useGetMatchById = (matchId, options = {}) => {
  return useQuery({
    queryKey: ["match", matchId],
    queryFn: async () => {
      try {
        console.log('Fetching match details for:', matchId);
        const response = await MatchService.getMatchById(matchId);
        console.log('Match details response:', response);
        return response;
      } catch (error) {
        console.error(`Error fetching match ${matchId}:`, error);
        throw error;
      }
    },
    enabled: !!matchId,
    ...options
  });
};

/**
 * Hook to fetch match summary data for completed matches
 * @param {string} matchId - Match ID
 * @returns {Object} - Query result with summary data (commentary + player stats)
 */
export const useGetMatchPlayerStats = (matchId) => {
  return useQuery({
    queryKey: ['matchPlayerStats', matchId],
    queryFn: async () => {
      try {
        const response = await MatchService.getMatchPlayerStats(matchId);
        return response;
      } catch (error) {
        console.error(`Error fetching player stats for match ${matchId}:`, error);
        throw error;
      }
    },
    enabled: !!matchId,
    staleTime: 2 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};

export const useGetMatchSummary = (matchId) => {
  return useQuery({
    queryKey: ["matchSummary", matchId],
    queryFn: async () => {
      try {
        console.log('Fetching match summary for:', matchId);
        const response = await MatchService.getMatchSummary(matchId);
        console.log('Match summary response:', response);
        return response;
      } catch (error) {
        console.error(`Error fetching match summary ${matchId}:`, error);
        throw error;
      }
    },
    enabled: !!matchId,
    staleTime: 5 * 60 * 1000, // 5 minutes (summary data doesn't change often)
    cacheTime: 30 * 60 * 1000, // 30 minutes
  });
};
